import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notify } from 'shared/lib/notification';
import { Button, Flex, MaterialInput, Typography } from 'shared/_ui';
import { useSendOtp, Email } from 'shared/api/otpApi';

import styles from './styles.module.scss';
import { getRouteOtpCheck } from 'shared/consts';
import i18next from 'i18next';
import { getStoredUtmTags } from 'shared/lib/utmTags';
import { clearAuthFlow, setAuthFlow } from 'shared/lib/authFlow';

type LoginFormProps = {
  authProviders?: () => React.ReactNode;
};

export const LoginForm: React.FC<LoginFormProps> = ({ authProviders }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [otplogin, { isLoading }] = useSendOtp();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Email>({
    mode: 'onSubmit',
    defaultValues: { email: '', languageCode: i18next.language },
  });

  const useOnSubmit = async (values: Email) => {
    const email = values.email.trim();
    const now = Date.now();
    const resendTime = Number(localStorage.getItem('resend-time') ?? 0);

    if (resendTime > now) {
      notify.error(
        `${t('authentication.otp.wait')}${Math.ceil(
          (resendTime - now) / 1000
        )}${t('authentication.otp.seconds')}`
      );
      return;
    }

    localStorage.setItem('user-email', email);
    setAuthFlow('login');

    await otplogin({
      ...values,
      email,
      languageCode: i18next.language,
      ...getStoredUtmTags(),
    })
      .unwrap()
      .then(() => {
        localStorage.setItem('resend-time', (Date.now() + 60000).toString());
        navigate(getRouteOtpCheck(pathname), { replace: true });
      })
      .catch(() => {
        localStorage.removeItem('user-email');
        clearAuthFlow();
        notify.error(t('authentication.login.error'));
      });
  };

  return (
    <Flex direction="column" spacing={20}>
      <form onSubmit={handleSubmit(useOnSubmit)}>
        <Typography.Text type="secondary" className="fix">
          {t('authentication.registration.otp')}
        </Typography.Text>
        <Flex direction="column" spacing={20} className={styles.wrapper}>
          <div className={styles.field}>
            <MaterialInput placeholder="Email" {...register('email')} />
            {errors.email && (
              <Typography.Text type="danger">
                {t(
                  errors.email?.message
                    ? errors.email?.message
                    : 'shared.error.base'
                )}
              </Typography.Text>
            )}
          </div>

          <Button
            loading={isLoading}
            type="primary"
            analyticsEventName="click_Login"
          >
            {t('authentication.login.loginButton')}
          </Button>
        </Flex>
      </form>
      {authProviders?.()}
    </Flex>
  );
};
